// Mandatory Disclaimer Modal - Scroll to Accept
// Shows on first visit, requires user to scroll through and accept
// Last Updated: October 2025

import React, { useState, useRef, useEffect } from 'react';
import { ASIC_DISCLAIMER } from '../ScenarioService.js';

export const DisclaimerModal = ({ isOpen, onAccept, onDecline }) => {
    const [hasScrolled, setHasScrolled] = useState(false);
    const [scrollPercentage, setScrollPercentage] = useState(0);
    const contentRef = useRef(null);

    useEffect(() => {
        if (isOpen && contentRef.current) {
            // Reset scroll state when modal opens
            setHasScrolled(false);
            setScrollPercentage(0);
        }
    }, [isOpen]);

    const handleScroll = (e) => {
        const element = e.target;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;
        
        // Calculate scroll percentage
        const totalScrollable = scrollHeight - clientHeight;
        const percentage = totalScrollable > 0 ? (scrollTop / totalScrollable) * 100 : 100;
        setScrollPercentage(percentage);
        
        // Consider "scrolled" if user has scrolled to at least 90% of content
        if (percentage >= 90) {
            setHasScrolled(true);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[100] sm:p-4">
            <div className="bg-white sm:rounded-lg shadow-2xl max-w-3xl w-full h-full sm:h-auto sm:max-h-[85vh] flex flex-col">
                {/* Header */}
                <div className="bg-red-600 text-white p-4 sm:p-6 sm:rounded-t-lg" style={{ paddingTop: 'max(env(safe-area-inset-top), 16px)' }}>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <svg className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                            <h2 className="text-lg sm:text-2xl font-bold">Important Legal Disclaimer</h2>
                            <p className="text-red-100 text-xs sm:text-sm mt-1">Please read carefully before using</p>
                        </div>
                    </div>
                </div>

                {/* Scroll Progress Indicator */}
                <div className="bg-gray-200 h-2">
                    <div 
                        className={`h-2 transition-all duration-300 ${
                            hasScrolled ? 'bg-green-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${scrollPercentage}%` }}
                    ></div>
                </div>

                {/* Scrollable Content */}
                <div 
                    ref={contentRef}
                    onScroll={handleScroll}
                    className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6"
                    style={{ 
                        maxHeight: 'calc(100vh - 280px)',
                        WebkitOverflowScrolling: 'touch',
                        overscrollBehavior: 'contain'
                    }}
                >
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 sm:p-4">
                        <p className="text-yellow-800 font-semibold text-sm sm:text-base leading-relaxed">
                            ⚠️ This calculator is for EDUCATIONAL PURPOSES ONLY - NOT financial advice
                        </p>
                    </div>

                    <div className="text-gray-700 whitespace-pre-line leading-relaxed text-sm sm:text-base">
                        {ASIC_DISCLAIMER}
                    </div>

                    <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 sm:p-6">
                        <h3 className="text-base sm:text-lg font-bold text-red-900 mb-3">⚠️ CRITICAL: What This Means for You</h3>
                        <ul className="space-y-3 text-red-800 text-sm leading-relaxed">
                            <li className="flex items-start gap-2">
                                <span className="text-red-600 font-bold mt-0.5 flex-shrink-0">•</span>
                                <span><strong>Not Financial Advice:</strong> This calculator does not consider your personal circumstances</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-red-600 font-bold mt-0.5">•</span>
                                <span><strong>Seek Professional Advice:</strong> Always consult a licensed financial adviser before making decisions</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-red-600 font-bold mt-0.5">•</span>
                                <span><strong>No Guarantees:</strong> Projections are estimates only - actual results will differ</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-red-600 font-bold mt-0.5">•</span>
                                <span><strong>Your Risk:</strong> You use this calculator entirely at your own risk</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-red-600 font-bold mt-0.5">•</span>
                                <span><strong>Verify Information:</strong> Always check official sources (ATO, Services Australia)</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                        <h4 className="font-semibold text-blue-900 mb-2">When You MUST Seek Professional Advice:</h4>
                        <ul className="text-blue-800 text-sm space-y-1 ml-4 list-disc">
                            <li>Before making any major financial decisions</li>
                            <li>Before starting or managing an SMSF</li>
                            <li>If you have complex tax situations</li>
                            <li>For Age Pension optimization strategies</li>
                            <li>For estate planning and death benefit nominations</li>
                        </ul>
                    </div>

                    {!hasScrolled && (
                        <div className="bg-orange-50 border border-orange-300 rounded-lg p-4 text-center sticky bottom-0">
                            <p className="text-orange-800 font-semibold flex items-center justify-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                </svg>
                                Please scroll to the bottom to continue
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer with Action Buttons */}
                <div className="border-t p-4 sm:p-6 bg-gray-50 sm:rounded-b-lg" 
                     style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}>
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
                        <div className="text-sm text-gray-600 text-center sm:text-left">
                            {hasScrolled ? (
                                <span className="text-green-600 font-semibold flex items-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    You may now proceed
                                </span>
                            ) : (
                                <span className="text-gray-500">Scroll to bottom to enable "Accept" button</span>
                            )}
                        </div>
                        <div className="flex gap-3 w-full sm:w-auto">
                            <button
                                onClick={onDecline}
                                className="flex-1 sm:flex-none px-6 py-4 sm:py-2 text-base sm:text-sm text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition touch-manipulation"
                                style={{ minHeight: '48px' }}
                            >
                                Decline
                            </button>
                            <button
                                onClick={onAccept}
                                disabled={!hasScrolled}
                                className={`flex-1 sm:flex-none px-6 py-4 sm:py-2 text-base sm:text-sm rounded-lg font-medium transition touch-manipulation ${
                                    hasScrolled 
                                        ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer' 
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                                }`}
                                style={{ minHeight: '48px' }}
                            >
                                I Understand and Accept
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DisclaimerModal;

